import React, { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { generateUsername } from '../services/username'
import { signUp, signIn } from '../services/auth'
import { track } from '../services/analytics'
import { findUserByUsername, sendKnock } from '../services/friends'
import { db } from '../services/firebase'
import { doc, onSnapshot } from 'firebase/firestore'

// ─────────────────────────────────────────────────────────────── WELCOME ─────

export function Welcome() {
  const nav = useNavigate()
  // living color — a triad that drifts perpetually around the wheel
  const [base, setBase] = React.useState(() => Math.random() * 360)
  React.useEffect(() => {
    let raf: number
    let last = performance.now()
    const tick = (now: number) => {
      const dt = now - last; last = now
      setBase((b) => (b + dt * 0.03) % 360) // ~12s per full rotation — clearly alive
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [])
  const hues = [base, (base + 120) % 360, (base + 240) % 360]
    .map((hh) => `hsl(${hh}, 100%, 62%)`)
  const [stats, setStats] = React.useState<{ tinglers?: number; feelings?: number }>({})
  React.useEffect(() => onSnapshot(doc(db, 'stats', 'global'), (s) => {
    if (s.exists()) setStats(s.data() as any)
  }), [])
  const showCount = (stats.tinglers || 0) >= 50
  const [showWhat, setShowWhat] = React.useState(false)
  // the drifting triad drives the brand elements too — everything breathes color
  const liveGradient = `linear-gradient(135deg, ${hues[0]}, ${hues[1]}, ${hues[2]})`
  const liveMint = hues[0]
  return (
    <div className="screen screen-center">
      <div className="orb" style={{ width: 340, height: 340, background: hues[0], top: -90, left: -90, opacity: 0.32 }} />
      <div className="orb" style={{ width: 300, height: 300, background: hues[1], bottom: -70, right: -70, animationDelay: '-3s', opacity: 0.3 }} />
      <div className="orb" style={{ width: 240, height: 240, background: hues[2], top: '40%', left: '36%', animationDelay: '-6s', opacity: 0.28 }} />

      <div className="fade-in" style={{ width: '100%', maxWidth: 380, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', zIndex: 2 }}>
        <div className="logo" style={{ fontSize: 56 }}>
          <span className="t">t</span>
          <span style={{
            display: 'inline-block',
            backgroundImage: liveGradient,
            WebkitBackgroundClip: 'text', backgroundClip: 'text',
            WebkitTextFillColor: 'transparent', color: 'transparent',
          }}>ingl</span>
          <span className="logo-dot" style={{ width: 10, height: 10, marginBottom: 42, marginLeft: -28, background: liveMint, boxShadow: `0 0 16px ${liveMint}` }} />
        </div>
        <div className="eyebrow" style={{ marginTop: 8 }}>feel everything · 1 second at a time</div>

        <div style={{ textAlign: 'center', margin: '38px 0 34px' }}>
          <h1 className="syne" style={{ fontWeight: 800, fontSize: 36, letterSpacing: '-0.05em', lineHeight: 1.06 }}>
            The world's most<br />human<br />
            <span style={{
              display: 'inline-block',
              backgroundImage: liveGradient,
              WebkitBackgroundClip: 'text', backgroundClip: 'text',
              WebkitTextFillColor: 'transparent', color: 'transparent',
            }}>1 second.</span>
          </h1>
          <p style={{ fontSize: 15, color: 'var(--text-dim)', lineHeight: 1.6, maxWidth: 280, margin: '14px auto 0' }}>
            A flash of color. A sound. A feeling sent from one human to another. No words needed.
          </p>
        </div>

        {showCount && (
          <div style={{
            textAlign: 'center', marginBottom: 22,
            fontFamily: 'Space Grotesk', fontSize: 12.5,
            color: 'rgba(255,255,255,0.65)', lineHeight: 1.7,
          }}>
            <span className="syne" style={{ fontWeight: 800, color: '#fff', fontSize: 14 }}>{stats.tinglers}</span> tinglers
            {stats.feelings ? <> · <span className="syne" style={{ fontWeight: 800, color: '#fff', fontSize: 14 }}>{stats.feelings.toLocaleString()}</span> feelings sent</> : null}
          </div>
        )}
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <button className="pill grad" onClick={() => nav('/signup')}
            style={{ background: liveGradient, boxShadow: `0 8px 32px ${hues[0]}55` }}>
            Get your username</button>
          <button className="pill ghost" onClick={() => nav('/login')}>I have an account</button>
        </div>

        <button onClick={() => setShowWhat(!showWhat)} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'rgba(255,255,255,0.5)', fontFamily: 'Space Grotesk',
          fontSize: 13, marginTop: 20, letterSpacing: '0.02em',
        }}>
          what is tingl? {showWhat ? '↑' : '→'}
        </button>

        {showWhat && (
          <div className="fade-in" style={{ marginTop: 20, width: '100%', display: 'flex', flexDirection: 'column', gap: 14 }}>
            {[
              { n: '01', h: 'pick a feeling', b: '12 living signatures — love, hype, miss you, 3am. no words needed.' },
              { n: '02', h: 'send it in a second', b: 'it lands on their phone as light, motion, sound & touch.' },
              { n: '03', h: 'connect by resonance', b: 'knock on someone. they answer with a vibe. that\'s the whole language.' },
            ].map((step, i) => (
              <div key={step.n} style={{
                display: 'flex', gap: 14, alignItems: 'flex-start',
                padding: '14px 16px', borderRadius: 16,
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.07)',
              }}>
                <span className="syne" style={{
                  fontSize: 13, fontWeight: 800,
                  color: hues[i], flexShrink: 0, marginTop: 1,
                }}>{step.n}</span>
                <div>
                  <div className="syne" style={{ fontWeight: 800, fontSize: 15, letterSpacing: '-0.02em' }}>{step.h}</div>
                  <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.55)', lineHeight: 1.5, marginTop: 2 }}>{step.b}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="eyebrow" style={{ marginTop: 34, fontSize: 9, textAlign: 'center', lineHeight: 2 }}>
          the vibe protocol™<br />
          tingl, a <a href="https://tinkrr.app" target="_blank" rel="noreferrer" style={{ color: 'var(--mint)', textDecoration: 'none' }}>Tinkrr</a> production
          &nbsp;·&nbsp; <a href="/about.html" style={{ color: 'inherit' }}>about</a>
          &nbsp;·&nbsp; <a href="/privacy.html" style={{ color: 'inherit' }}>privacy</a>
          <br />
          <a href="https://instagram.com/tingl.me" target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>instagram</a>
          &nbsp;·&nbsp; <a href="https://x.com/tinglme" target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>x</a>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────── SIGNUP ──────

export function Signup() {
  const nav = useNavigate()
  const [params] = useSearchParams()
  const knockTarget = params.get('knock')
  const [username, setUsername] = useState(generateUsername())
  const [password, setPassword] = useState('')
  const [recovery, setRecovery] = useState('')
  const [showRecovery, setShowRecovery] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const regen = () => {
    setUsername(generateUsername())
    if ('vibrate' in navigator) navigator.vibrate(15)
  }

  const submit = async () => {
    setError('')
    if (password.length < 6) { setError('Password needs 6+ characters'); return }
    setBusy(true)
    try {
      const rec = recovery.includes('@') ? { email: recovery } : recovery ? { phone: recovery } : undefined
      await signUp(username, password, rec)
      track('sign_up')
      if (knockTarget) {
        try {
          const found = await findUserByUsername(knockTarget)
          if (found) { await sendKnock(found.uid); track('knock_sent', { via: 'invite_link' }) }
        } catch {}
      }
      nav('/home')
    } catch (e: any) {
      const msg = e?.message || 'Something went wrong'
      if (msg.includes('already-exists') || msg.includes('taken')) {
        setError('That username just got taken — hit regenerate')
      } else setError(msg.slice(0, 140))
    } finally { setBusy(false) }
  }

  return (
    <div className="screen screen-center">
      <div className="orb" style={{ width: 280, height: 280, background: 'var(--sky)', top: -80, right: -80 }} />
      <div className="fade-in" style={{ width: '100%', maxWidth: 380, position: 'relative', zIndex: 2 }}>
        <button onClick={() => nav('/')} className="eyebrow" style={{ background: 'none', border: 'none', cursor: 'pointer', marginBottom: 26 }}>← back</button>

        <div className="eyebrow">{knockTarget ? `knocking on ${knockTarget} once you're in` : 'your identity in the vibe protocol'}</div>
        <h2 className="syne" style={{ fontWeight: 800, fontSize: 28, letterSpacing: '-0.04em', margin: '8px 0 26px' }}>
          Meet yourself.
        </h2>

        {/* the generated name — the fun moment */}
        <div className="card" style={{ padding: '26px 22px', textAlign: 'center', marginBottom: 14 }}>
          <div className="syne" style={{
            fontWeight: 800, fontSize: 'clamp(24px, 7.5vw, 32px)', letterSpacing: '-0.03em',
            background: 'linear-gradient(135deg, var(--mint), var(--sky))',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          }}>
            {username}
          </div>
          <button onClick={regen} style={{
            marginTop: 14, background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.1)', borderRadius: 100,
            color: 'rgba(255,255,255,0.7)', padding: '9px 20px',
            fontFamily: 'Space Grotesk', fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}>
            ✦ regenerate
          </button>
        </div>

        <input
          className="field" type="password" placeholder="Pick a password (6+ characters)"
          value={password} onChange={(e) => setPassword(e.target.value)}
          style={{ marginBottom: 12 }}
        />

        {!showRecovery ? (
          <button onClick={() => setShowRecovery(true)} style={{
            background: 'none', border: 'none', color: 'var(--text-faint)',
            fontFamily: 'Space Grotesk', fontSize: 13, cursor: 'pointer',
            marginBottom: 18, textDecoration: 'underline',
          }}>
            add recovery email or phone (optional)
          </button>
        ) : (
          <input
            className="field" placeholder="Email or phone — only for password recovery"
            value={recovery} onChange={(e) => setRecovery(e.target.value)}
            style={{ marginBottom: 18 }}
          />
        )}

        {error && <div style={{ color: 'var(--pink)', fontSize: 13, marginBottom: 14 }}>{error}</div>}

        <button className="pill grad" onClick={submit} disabled={busy}>
          {busy ? 'entering the protocol…' : `I am ${username}`}
        </button>

        <p style={{ fontSize: 12, color: 'var(--text-faint)', textAlign: 'center', marginTop: 16, lineHeight: 1.6 }}>
          No email. No phone. No real name.<br />Just you and a feeling.
        </p>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────── LOGIN ───────

export function Login() {
  const nav = useNavigate()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const submit = async () => {
    setError('')
    setBusy(true)
    try {
      await signIn(username, password)
      track('login')
      nav('/home')
    } catch (e: any) {
      const msg = e?.message || ''
      if (msg.includes('not-found')) setError('Username not found')
      else if (msg.includes('unauthenticated') || msg.includes('Wrong')) setError('Wrong password')
      else setError((msg || 'Could not sign in').slice(0, 140))
    } finally { setBusy(false) }
  }

  return (
    <div className="screen screen-center">
      <div className="orb" style={{ width: 260, height: 260, background: 'var(--violet)', bottom: -70, left: -70 }} />
      <div className="fade-in" style={{ width: '100%', maxWidth: 380, position: 'relative', zIndex: 2 }}>
        <button onClick={() => nav('/')} className="eyebrow" style={{ background: 'none', border: 'none', cursor: 'pointer', marginBottom: 26 }}>← back</button>

        <div className="eyebrow">welcome back to the protocol</div>
        <h2 className="syne" style={{ fontWeight: 800, fontSize: 28, letterSpacing: '-0.04em', margin: '8px 0 26px' }}>
          Who are you?
        </h2>

        <input
          className="field" placeholder="Your username (e.g. CosmicPickle)"
          value={username} onChange={(e) => setUsername(e.target.value)}
          autoCapitalize="none" style={{ marginBottom: 12 }}
        />
        <input
          className="field" type="password" placeholder="Password"
          value={password} onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
          style={{ marginBottom: 18 }}
        />

        {error && <div style={{ color: 'var(--pink)', fontSize: 13, marginBottom: 14 }}>{error}</div>}

        <button className="pill grad" onClick={submit} disabled={busy || !username || !password}>
          {busy ? 'one sec…' : 'Enter'}
        </button>
      </div>
    </div>
  )
}
